import {getEventDate, getEventTime, getVenueInfo} from '@stubhub/react-event-helper';

import {buildEventsJSONLD, SCHEMA_ORG_PREFIX, checkJSONLDAndMicroDataEnabled} from '../../../helpers/jsonld-helper';
import {GROUPING_IDS} from '../../../utils/constants';

/**
 * Determines if a URL is external (different hostname) and has no tickets available.
 * Used to decide whether to show external links for events without tickets.
 *
 * @param {number} [totalTickets=0] - The total number of available tickets
 * @param {string} url - The URL to check for external domain
 * @returns {boolean} True if the URL is external and has no tickets, false otherwise
 */
export const isExternalWithoutTickets = (totalTickets = 0, url) => {
  if (
    typeof __CLIENT__ !== 'undefined' &&
    __CLIENT__ &&
    typeof window !== 'undefined' &&
    window.location &&
    window.location.origin &&
    window.location.hostname
  ) {
    const fullURL = new URL(url, window.location.origin);

    return fullURL.hostname !== window.location.hostname && totalTickets < 1;
  }

  return true;
};

/**
 * Processes event date and time information for display.
 * Combines date and time processing to return formatted display components.
 *
 * @param {Object} event - The event object containing date/time information
 * @param {string} userCountryCode - User's country code for localized time formatting
 * @returns {Object} Object containing processed date and time display elements
 * @returns {string} returns.eventDate - Formatted event date string
 * @returns {React.Element|null} returns.timeDisplay - Formatted time component or null
 */
export const processEventDateTime = (event, userCountryCode) => {
  const eventDate = getEventDate(event);
  const timeDisplay = getEventTime(event, userCountryCode);

  return {
    eventDate,
    timeDisplay,
  };
};

/**
 * Calculates and formats distance display for event venues.
 * Rounds distance to nearest mile and formats for display with unit suffix.
 *
 * @param {Object} event - Event object containing distance information
 * @param {number|null} event.distance - Distance to venue in miles (can be null)
 * @param {boolean} showDistance - Whether distance should be displayed
 * @returns {string|null} Formatted distance string with unit (e.g., " (5 mi)") or null
 */
export const calculateDistanceDisplay = (event, showDistance) => {
  let {distance = null} = event;
  distance = distance !== null ? Math.ceil(distance) : distance;
  const distanceVal = distance !== null ? ` (${distance} mi)` : '';

  return distance !== null && showDistance ? distanceVal : null;
};

/**
 * Processes venue information for display including location, name, country, and distance.
 * Combines venue data with distance information to create a comprehensive venue display object.
 *
 * @param {Object} event - Event object containing venue information
 * @param {string|null} distanceDisplay - Formatted distance string (e.g., " (5 mi)")
 * @returns {Object} Object containing processed venue information
 * @returns {string} returns.venueLocation - Venue location/address
 * @returns {string} returns.venueName - Name of the venue
 * @returns {string} returns.venueCountry - Country where venue is located
 * @returns {Object} returns.venueDisplay - Complete venue display object
 * @returns {string} returns.venueDisplay.name - Venue name for display
 * @returns {string} returns.venueDisplay.location - Venue location for display
 * @returns {string} returns.venueDisplay.country - Venue country for display
 * @returns {string|null} returns.venueDisplay.distance - Distance display string
 */
export const processVenueInfo = (event, distanceDisplay) => {
  const {venueLocation, venueName, venueCountry} = getVenueInfo(event);

  return {
    venueLocation,
    venueName,
    venueCountry,
    venueDisplay: {
      name: venueName,
      location: venueLocation,
      country: venueCountry,
      distance: distanceDisplay,
    },
  };
};

/**
 * Processes pricing logic and determines display behavior based on feature flags and event characteristics.
 * Handles various pricing scenarios including fee inclusion, special events, and regional variations.
 *
 * @param {Object} event - Event object containing ticket and venue information
 * @param {Object} event.ticketInfo - Ticket pricing information
 * @param {number} event.ticketInfo.totalTickets - Total number of available tickets
 * @param {string} event.ticketInfo.currencyCode - Currency code for pricing
 * @param {number} event.ticketInfo.minPrice - Minimum price including fees
 * @param {number} event.ticketInfo.minListPrice - Minimum list price excluding fees
 * @param {Array} event.groupings - Array of event groupings/categories
 * @param {string} event.sourceId - Source identifier for the event
 * @param {Object} event.venue - Venue information
 * @param {string} event.venue.country - Country where venue is located
 * @param {boolean} isPriceIncludeFees - Flag indicating if prices should include fees
 * @param {boolean} hideTicketBisPriceFlag - Flag to hide Ticket Bis price for specific sources
 * @param {Object} globalRegistry - Global configuration registry for feature flags
 * @returns {Object} Object containing processed pricing information
 * @returns {number} returns.totalTickets - Total available tickets
 * @returns {string} returns.ticketCurrencyCode - Currency code for display
 * @returns {number} returns.price - Final price to display (with or without fees)
 * @returns {boolean} returns.isSeePrices - Whether to show "See Prices" instead of actual price
 * @returns {string} returns.webTLD - Web top-level domain for regional handling
 */
export const processPricingLogic = (event, isPriceIncludeFees, hideTicketBisPriceFlag, globalRegistry) => {
  const {ticketInfo = {}, groupings = [], sourceId, venue = {}} = event;
  const {totalTickets, currencyCode: ticketCurrencyCode, minPrice, minListPrice} = ticketInfo;

  const hideTicketBisPrice = hideTicketBisPriceFlag && sourceId === '4001';

  const showSeePricesForSuperBowlEvent =
    groupings.findIndex((grouping) => grouping.id === GROUPING_IDS.SUPERBOWL) !== -1;

  const webTLD = globalRegistry.getDefaultWebTLD();
  const showSeePricesForCA = webTLD === 'ca' && venue.country === 'CA';

  const price = isPriceIncludeFees ? minPrice : minListPrice;
  const isSeePrices = hideTicketBisPrice || showSeePricesForCA || showSeePricesForSuperBowlEvent;

  return {
    totalTickets,
    ticketCurrencyCode,
    price,
    isSeePrices,
    webTLD,
  };
};

/**
 * Generates microdata markup and JSON-LD structured data for events.
 * Creates Schema.org compliant markup for SEO and rich snippets when enabled by feature flags.
 *
 * @param {Object} globalRegistry - Global configuration registry for feature flags and settings
 * @param {Object} event - Event object containing event information for structured data
 * @param {string} webTLD - Web top-level domain for region-specific configuration
 * @returns {Object} Object containing microdata and JSON-LD information
 * @returns {boolean} returns.microDataToggle - Whether microdata generation is enabled
 * @returns {Object} returns.microDataMarkup - HTML microdata attributes for the event
 * @returns {string} returns.microDataMarkup.itemType - Schema.org itemType for the event
 * @returns {Object|undefined} returns.jsonld - JSON-LD structured data object
 */
export const generateMicroDataMarkup = (globalRegistry, event, webTLD) => {
  let microDataMarkup = {};
  let jsonld;

  const {microDataToggle} = checkJSONLDAndMicroDataEnabled(globalRegistry, event);
  const redirectionDomain = globalRegistry.getPropertyValue(`tb.eventpage.url.domain.${webTLD}`) || '';

  if (microDataToggle) {
    const eventJSONLD = buildEventsJSONLD([event], webTLD, 1, redirectionDomain);
    if (eventJSONLD && eventJSONLD.length && eventJSONLD[0]) {
      [jsonld] = eventJSONLD;
      microDataMarkup = {
        itemType: `${SCHEMA_ORG_PREFIX}${jsonld['@type']}`,
      };
    }
  }

  return {
    microDataToggle,
    microDataMarkup,
    jsonld,
  };
};
